import React, { useEffect, useState } from "react";
import { AxiosResponse } from "axios";
import { Form, Formik } from "formik";
import * as yup from "yup";
import http from "../../../utils/http";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormLabel,
  TextField,
  Typography,
} from "@mui/material";
import { HalfInputWrapper, LabelStyle } from "../../Common/styles/form";
import { commonContainerWrapper } from "../../Common/styles/flex";
import { ModalActionButtonStyles } from "../../Common/styles/modal";
import Loader from "../../Common/assets/Loader";
import { useAppDispatch } from "../../../Redux/hooks";
import { setUserName } from "../../../Redux/reducers/userSlice";

const schema = yup.object().shape({
  firstName: yup.string().required("First name is Required"),
  lastName: yup.string().required("Last name is Required"),
});

const General = () => {
  const dispatch = useAppDispatch();
  const [buttonLoader, setButtonLoader] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [toggle, setToggle] = useState(false);
  const [data, setData] = useState<any>({});

  const refreshPage = () => {
    setToggle((prev) => !prev);
  };

  useEffect(() => {
    const fetchGeneralData = async () => {
      try {
        setLoading(true);
        let url = `/users/me`;
        const res: AxiosResponse = await http.get(url);
        const data = res.data?.data;
        const newData = {
          firstName: data?.firstName || "",
          lastName: data?.lastName || "",
          email: data?.email || "",
        };
        setData(newData);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    };
    fetchGeneralData();
  }, [setLoading, toggle]);

  const submitHandler = async (values: any) => {
    try {
      setButtonLoader(true);
      const body: any = {
        firstName: values?.firstName,
        lastName: values?.lastName,
      };
      let res: AxiosResponse;

      res = await http.patch(`/users/me`, body);

      toastMessage("success", res.data.message);
      setButtonLoader(false);
      dispatch(setUserName(body));
      refreshPage();
    } catch (err) {
      setButtonLoader(false);
      errorToastMessage(err as Error);
    }
  };

  const handleCancel = async () => {
    try {
      setLoading(true);
      let url = `/users/me`;
      const res: AxiosResponse = await http.get(url);
      const data = res.data?.data;
      const newData = {
        firstName: data?.firstName || "",
        lastName: data?.lastName || "",
        email: data?.email || "",
      };
      setData(newData);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      errorToastMessage(err as Error);
    }
  };

  return (
    <>
      {loading ? (
        <Backdrop
          open={true}
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        >
          <Loader />
        </Backdrop>
      ) : (
        <Box sx={commonContainerWrapper}>
          <Typography
            variant="h2"
            fontWeight="regular"
            color="text.primary"
            mb={3}
          >
            Update your personal details and contact information.
          </Typography>
          <Formik
            initialValues={{
              firstName: data?.firstName || "",
              lastName: data?.lastName || "",
            }}
            validationSchema={schema}
            onSubmit={(values) => {
              submitHandler(values);
            }}
          >
            {({ errors, touched, getFieldProps }: any) => (
              <Form>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <FormControl sx={HalfInputWrapper}>
                    <FormLabel sx={LabelStyle} htmlFor="first-name">
                      First Name <span style={{ color: "#f16262" }}>*</span>
                    </FormLabel>
                    <TextField
                      id="first-name"
                      placeholder="First Name"
                      {...getFieldProps("firstName")}
                      error={
                        touched?.firstName && errors?.firstName ? true : false
                      }
                      helperText={
                        touched?.firstName && errors?.firstName
                          ? errors?.firstName
                          : " "
                      }
                    />
                  </FormControl>

                  <FormControl sx={HalfInputWrapper}>
                    <FormLabel sx={LabelStyle} htmlFor="last-name">
                      Last Name <span style={{ color: "#f16262" }}>*</span>
                    </FormLabel>
                    <TextField
                      id="last-name"
                      placeholder="Last Name"
                      {...getFieldProps("lastName")}
                      error={
                        touched?.lastName && errors?.lastName ? true : false
                      }
                      helperText={
                        touched?.lastName && errors?.lastName
                          ? errors?.lastName
                          : " "
                      }
                    />
                  </FormControl>

                  <FormControl sx={HalfInputWrapper}>
                    <FormLabel sx={LabelStyle} htmlFor="email">
                      Email
                    </FormLabel>
                    <Typography variant="body1">{data?.email}</Typography>
                  </FormControl>

                  <Box sx={ModalActionButtonStyles}>
                    {!buttonLoader ? (
                      <>
                        <Button variant="outlined" onClick={handleCancel}>
                          Cancel
                        </Button>
                        <Button variant="contained" type="submit">
                          Save
                        </Button>
                      </>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          height: "40px",
                        }}
                      >
                        <CircularProgress size={25} />
                      </Box>
                    )}
                  </Box>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      )}
    </>
  );
};

export default General;
